import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import type { AttachPointType, ListingCommentData } from '@app/api/collaboration';
import type { CommentListType } from '@app/features/comments/components/CommentsSidebar/types';
import { CommentList } from '@app/features/comments/components/CommentsSidebar/types';
import {
    createKeyCommentList,
    createProjectCommentList,
    createThreadCommentList,
    createTranslationCommentList,
    getTranslationAttachPointComponents,
} from '@app/features/comments/components/CommentsSidebar/utils';
import commentsActions from '@app/features/comments/store/actions';
import { saveCommentListState } from '@app/features/comments/store/reducers';
import { getBranch, getCommentListStack, getCommentState, getProject } from '@app/selectors';
import type { RootState } from '@app/types';
import track, { AnalyticsEventName } from '@app/utils/analytics';
import assertIsNever from '@app/utils/assertIsNever';
import { navigateToPath } from '@app/utils/location';

const commentsMiddleware = createListenerMiddleware<RootState>();

commentsMiddleware.startListening({
    // I have no idea why this parameter type is wrong - but it's valid
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    matcher: isAnyOf(...Object.values(commentsActions)),
    effect: (action, api) => saveCommentListState(getCommentState(api.getState())),
});

const getCommentStackUpdateActionForNextListType = (
    comment: ListingCommentData,
    commentList: CommentListType,
    nextListType: CommentList,
) => {
    switch (nextListType) {
        case CommentList.Project:
            return commentsActions.commentListOpened({
                commentList: createKeyCommentList({
                    projectId: comment.projectId,
                    keyName: comment.attachPointName,
                    keyId: comment.attachPointId,
                }),
            });

        case CommentList.Key:
            // We are already here - Don't open another key comment list
            if (commentList.type === CommentList.Key && comment.attachPointId === commentList.keyId) {
                return null;
            }

            return commentsActions.commentListOpened({
                commentList: createKeyCommentList({
                    projectId: comment.projectId,
                    keyName: comment.attachPointName,
                    keyId: comment.attachPointId,
                }),
            });

        case CommentList.Translation: {
            if (commentList.type === CommentList.Translation && comment.attachPointId === commentList.translationId) {
                return null;
            }

            const [keyName, language] = getTranslationAttachPointComponents(comment.attachPointName);

            return commentsActions.commentListOpened({
                commentList: createTranslationCommentList({
                    projectId: comment.projectId,
                    translationId: comment.attachPointId,
                    keyName,
                    language,
                }),
            });
        }

        case CommentList.Thread:
            if (commentList.type === CommentList.Thread && comment.id === commentList.parentCommentId) {
                return null;
            }

            return commentsActions.commentListOpened({
                commentList: createThreadCommentList({
                    projectId: comment.projectId,
                    attachPointName: comment.attachPointName,
                    attachPointId: comment.attachPointId,
                    attachPointType: comment.attachPointType,
                    parentCommentId: comment.id,
                    resolved: comment.resolved,
                }),
            });

        default:
            return assertIsNever(nextListType);
    }
};

const getStackUpdateAction = (comment: ListingCommentData, commentList: CommentListType, nextListType: CommentList) => {
    switch (comment.attachPointType) {
        case 'projects': {
            if (comment.projectId === commentList.projectId) {
                // Nothing to do, already in given project.
                return null;
            }

            return commentsActions.newCommentStackStarted({
                commentList: createProjectCommentList({
                    projectId: comment.projectId,
                    showResolvedComments: false,
                    hasComments: undefined,
                }),
            });
        }

        case 'keys':
        case 'translations':
            return getCommentStackUpdateActionForNextListType(comment, commentList, nextListType);

        default:
            return assertIsNever(comment.attachPointType);
    }
};

const focusOnAttachPoint = (attachPointType: AttachPointType, attachPointName: string) => {
    switch (attachPointType) {
        case 'projects':
            break;

        case 'keys':
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.focusOnKey?.(attachPointName);
            break;

        case 'translations': {
            const [keyName] = getTranslationAttachPointComponents(attachPointName);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.focusOnKey?.(keyName);
            break;
        }

        default:
            assertIsNever(attachPointType);
    }
};

const performRedirectForNewCommentList = (
    comment: ListingCommentData,
    previousCommentList: CommentListType,
    currentCommentList: CommentListType,
    currenProjectId: string,
    currentProjectBranchName: string | null | undefined,
) => {
    switch (currentCommentList.type) {
        case CommentList.Project: {
            if (comment.projectId !== previousCommentList.projectId) {
                navigateToPath(`/project/${comment.projectId}/`);
            }

            break;
        }
        case CommentList.Key:
        case CommentList.Translation:
        case CommentList.Thread: {
            // Check if the user currently viewing Editor tab within project
            const isInEditorTab = window.location.pathname.split('/')[1] === 'project';

            const isOnEditorPage = comment.masterProjectId === currenProjectId;
            const isOnCorrectBranch = comment.branchName ? comment.branchName === currentProjectBranchName : true;

            if (isOnEditorPage && isOnCorrectBranch && isInEditorTab) {
                focusOnAttachPoint(comment.attachPointType, comment.attachPointName);
            } else {
                let focusKeyUrl = `/project/${comment.masterProjectId}/`;

                focusKeyUrl +=
                    comment.attachPointType === 'translations'
                        ? `?t=${comment.attachPointId}`
                        : `?k=${comment.attachPointId}`;

                focusKeyUrl += comment.branchName ? `&branch=${comment.branchName}` : '';

                navigateToPath(focusKeyUrl);
            }
            break;
        }

        default:
            assertIsNever(currentCommentList);
    }
};

commentsMiddleware.startListening({
    actionCreator: commentsActions.backToPreviousCommentList,
    effect: (_, api) => {
        // On back to previous comment list we want to clear translation editor filter
        // If current active stack is of project type, then there's no focused key
        const currentActiveStack = getCommentState(api.getState()).commentListStack[0];
        if (currentActiveStack.type === CommentList.Project) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.focusOutKey?.();
        }
    },
});

// Track thread list opening event
// This doesn't listen for thread list open caused by URL navigation (email notification link)
// We initiate the state from the session storage check app/features/comments/store/comments/reducers.ts:92
commentsMiddleware.startListening({
    actionCreator: commentsActions.commentListOpened,
    effect: ({ payload }) => {
        const { commentList } = payload;

        if (commentList.type === CommentList.Thread) {
            track(AnalyticsEventName.THREAD_OPENED, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                project_id: commentList.projectId,
            });
        }
    },
});

commentsMiddleware.startListening({
    actionCreator: commentsActions.commentFocusedInEditor,
    effect: async ({ payload }, api) => {
        const { comment, commentIndex, nextListType } = payload;
        const commentList = getCommentListStack(api.getState())[0];

        await api.dispatch(
            commentIndex !== undefined
                ? commentsActions.updateActiveCommentListScrollPosition({ scrollPosition: commentIndex })
                : commentsActions.removeActiveCommentListScrollPosition(),
        );

        const action = getStackUpdateAction(comment, commentList, nextListType);
        if (action !== null) {
            await api.dispatch(action);
        }

        const updatedState = api.getState();

        saveCommentListState(getCommentState(updatedState));
        performRedirectForNewCommentList(
            comment,
            commentList,
            getCommentListStack(updatedState)[0],
            getProject(updatedState).id,
            getBranch(updatedState)?.branchName,
        );
    },
});

export default commentsMiddleware;
